import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ScanQrIcon from '../icons/ScanQrIcon';
import { WalletQuickPlusIcon, WalletQuickReceiveIcon, WalletQuickSendIcon } from '../icons/WalletQuickActionIcons';
import { useLandingLanguage } from '../../context/LandingLanguageContext';

/** Stroke icons for tx rows / toasts — crisp at any DPI vs emoji. */
function TxPoolGlyph({ kind, size = 17, stroke = 'rgba(232, 218, 188, 0.9)' }) {
  const s = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 1.65,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (kind) {
    case 'person':
      return (
        <svg {...s}>
          <circle cx="12" cy="8" r="3.25" />
          <path d="M5.5 20.5v-.9A4.5 4.5 0 0 1 10 15h4a4.5 4.5 0 0 1 4.5 4.6v.9" />
        </svg>
      );
    case 'coffee':
      return (
        <svg {...s}>
          <path d="M5 9h11v3.5a3.5 3.5 0 0 1-3.5 3.5H8.5A3.5 3.5 0 0 1 5 12.5V9z" />
          <path d="M16 9h2a2.5 2.5 0 0 1 0 5h-2" />
          <path d="M8 6.5V4M12 6.5V4" />
        </svg>
      );
    case 'card':
      return (
        <svg {...s}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18" />
        </svg>
      );
    case 'bag':
      return (
        <svg {...s}>
          <path d="M9 11V8a3 3 0 0 1 6 0v3" />
          <path d="M6 11h12v9a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-9z" />
        </svg>
      );
    case 'car':
      return (
        <svg {...s}>
          <path d="M5 17h14l-1.2-5.6a1 1 0 0 0-1-.8H7.2a1 1 0 0 0-1 .8L5 17z" />
          <circle cx="7.5" cy="17" r="1.5" />
          <circle cx="16.5" cy="17" r="1.5" />
          <path d="M3 17h2M19 17h2" />
        </svg>
      );
    case 'bank':
      return (
        <svg {...s}>
          <path d="M3 21h18M5 21V10.5l7-4.5 7 4.5V21" />
          <path d="M9 21v-5h6v5" />
        </svg>
      );
    case 'utensils':
      return (
        <svg {...s}>
          <path d="M9 3v10a1.5 1.5 0 0 0 3 0V3M9 3h3" />
          <path d="M17 3v14" />
          <path d="M17 3c.9 0 1.5.8 1.5 1.7V7c0 .9-.6 1.6-1.5 1.7" />
        </svg>
      );
    case 'cart':
      return (
        <svg {...s}>
          <circle cx="9" cy="20" r="1.25" />
          <circle cx="18" cy="20" r="1.25" />
          <path d="M3 4h2l1.6 10.4A1.5 1.5 0 0 0 8.1 16h9.4a1.5 1.5 0 0 0 1.5-1.2L21 7H6.2" />
        </svg>
      );
    default:
      return (
        <svg {...s}>
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
  }
}

const TX_POOL_DEF = [
  { nameKey: 'youssef', amount: 150, icon: 'person', positive: true },
  { nameKey: 'cafeBahia', amount: -75, icon: 'coffee', positive: false },
  { nameKey: 'cihTopUp', amount: 500, icon: 'card', positive: true },
  { nameKey: 'mariam', amount: 200, icon: 'person', positive: true },
  { nameKey: 'hassan', amount: 350, icon: 'person', positive: true },
  { nameKey: 'souk', amount: -45, icon: 'bag', positive: false },
  { nameKey: 'taxi', amount: -28, icon: 'car', positive: false },
  { nameKey: 'fatima', amount: 120, icon: 'person', positive: true },
  { nameKey: 'barid', amount: 1000, icon: 'bank', positive: true },
  { nameKey: 'restaurant', amount: -180, icon: 'utensils', positive: false },
  { nameKey: 'omar', amount: 600, icon: 'person', positive: true },
  { nameKey: 'marjane', amount: -245, icon: 'cart', positive: false },
];

/**
 * Live wallet demo used in the hero and the app-experience slideshow (step 0).
 */
const HeroWalletPhoneScreen = () => {
  const { t, locale } = useLandingLanguage();
  const INIT_BAL = 18224;
  const [displayBal, setDisplayBal] = useState(INIT_BAL);
  const balRef = useRef(INIT_BAL);
  const rafRef = useRef(null);

  const [recents, setRecents] = useState([
    { id: 'r1', nameKey: 'cafeBoulevard', amount: -38, positive: false, icon: 'coffee', timeKey: 'h1' },
    { id: 'r2', nameKey: 'topUpCard', amount: 500, positive: true, icon: 'card', timeKey: 'h2' },
    { id: 'r3', nameKey: 'marjane', amount: -245, positive: false, icon: 'cart', timeKey: 'h3' },
  ]);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const animateTo = (target) => {
    const from = balRef.current;
    const start = performance.now();
    const dur = 900;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      const val = Math.round(from + (target - from) * e);
      setDisplayBal(val);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else balRef.current = target;
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const fire = () => {
      const tx = TX_POOL_DEF[Math.floor(Math.random() * TX_POOL_DEF.length)];
      const newBal = Math.max(500, balRef.current + tx.amount);
      animateTo(newBal);
      const id = `t-${Date.now()}`;
      setToast({ ...tx, id });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 2600);
      setRecents((prev) => [{ ...tx, id, timeKey: 'justNow' }, ...prev.slice(0, 2)]);
    };
    const t0 = setTimeout(fire, 1200);
    const iv = setInterval(fire, 3400);
    return () => {
      clearTimeout(t0);
      clearInterval(iv);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const numLocale = locale === 'fr' ? 'fr-FR' : locale === 'ar' ? 'ar-MA' : 'en-US';
  const fmt = (n) => Math.abs(n).toLocaleString(numLocale);
  const balInt = Math.floor(displayBal).toLocaleString(numLocale);

  return (
    <>
      <style>{`
        @keyframes pulse-ring-hero {
          0%   { transform: scale(0.92); opacity: 0.5; }
          100% { transform: scale(1.32); opacity: 0; }
        }
      `}</style>
      <div
        style={{
          background: '#0F0F0F',
          height: '100%',
          fontFamily: 'Inter, sans-serif',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AnimatePresence>
          {toast && (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.96 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'absolute',
                top: '8px',
                left: '10px',
                right: '10px',
                zIndex: 30,
                background: toast.positive ? 'rgba(10,30,20,0.97)' : 'rgba(20,15,5,0.97)',
                border: `1px solid ${toast.positive ? 'rgba(74,222,128,0.3)' : 'rgba(212,175,55,0.3)'}`,
                borderRadius: '12px',
                padding: '8px 11px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 4px 20px ${toast.positive ? 'rgba(74,222,128,0.15)' : 'rgba(212,175,55,0.15)'}`,
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '8px',
                  flexShrink: 0,
                  background: toast.positive ? 'rgba(74,222,128,0.15)' : 'rgba(212,175,55,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 0,
                }}
              >
                <TxPoolGlyph
                  kind={toast.icon}
                  size={16}
                  stroke={toast.positive ? 'rgba(180, 230, 200, 0.95)' : 'rgba(240, 210, 130, 0.95)'}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    color: '#F5F1EA',
                    fontSize: '9.5px',
                    fontWeight: '700',
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t(`txPool.${toast.nameKey}`)}
                </p>
                <p style={{ color: '#8B93A7', fontSize: '8.5px', margin: 0 }}>{t('phone.simopayLine')}</p>
              </div>
              <span style={{ color: toast.positive ? '#4ADE80' : '#D4AF37', fontSize: '11px', fontWeight: '800', flexShrink: 0 }}>
                {toast.positive ? '+' : '−'}
                {fmt(toast.amount)} MAD
              </span>
              <div style={{ position: 'relative', width: '6px', height: '6px', flexShrink: 0 }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: toast.positive ? '#4ADE80' : '#D4AF37',
                    animation: 'pulse-ring-hero 1.5s ease-out infinite',
                  }}
                />
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: toast.positive ? '#4ADE80' : '#D4AF37',
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <p style={{ color: '#8B93A7', fontSize: '11px', margin: 0 }}>Salam,</p>
            <p style={{ color: '#F5F1EA', fontSize: '16px', fontWeight: '700', margin: 0, letterSpacing: '-0.4px' }}>Ahmed</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '11px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="1.8">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
              </div>
              <AnimatePresence>
                {toast && (
                  <motion.div
                    key="notif-dot"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#D4AF37',
                      border: '1.5px solid #0F0F0F',
                    }}
                  />
                )}
              </AnimatePresence>
            </div>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #D4AF37, #B8962E)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: '800',
                color: '#0F0F0F',
              }}
            >
              A
            </div>
          </div>
        </div>

        <div style={{ padding: '0 14px 14px', flexShrink: 0 }}>
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.05) 50%, rgba(255,255,255,0.03) 100%)',
              border: '1px solid rgba(212,175,55,0.22)',
              borderRadius: '20px',
              padding: '16px 18px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-24px',
                right: '-24px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                border: '1px solid rgba(212,175,55,0.08)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '-16px',
                left: '-16px',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                border: '1px solid rgba(212,175,55,0.06)',
                pointerEvents: 'none',
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '6px',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <p
                style={{
                  color: '#8B93A7',
                  fontSize: '9px',
                  margin: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: '600',
                }}
              >
                {t('phone.walletBalance')}
              </p>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>

            <p
              style={{
                color: '#D4AF37',
                fontSize: '30px',
                fontWeight: '800',
                margin: '0 0 2px',
                letterSpacing: '-1.5px',
                lineHeight: 1,
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'baseline',
              }}
            >
              {balInt}
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#8B93A7', marginLeft: '4px' }}>.00 MAD</span>
            </p>

            <p style={{ color: '#8B93A7', fontSize: '10px', fontWeight: '500', margin: '4px 0 10px', position: 'relative', zIndex: 1 }}>
              ahmed@simopay
            </p>

            <div
              style={{
                display: 'inline-flex',
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.18)',
                borderRadius: '100px',
                padding: '3px 10px',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <span style={{ color: '#D4AF37', fontSize: '9px', fontWeight: '600' }}>{t('phone.thisMonth')}</span>
            </div>
          </div>
        </div>

        <div style={{ padding: '0 14px 14px', flexShrink: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px' }}>
            {[
              { label: t('phone.send'), icon: <WalletQuickSendIcon size={18} /> },
              { label: t('phone.receive'), icon: <WalletQuickReceiveIcon size={18} /> },
              { label: t('phone.topUp'), icon: <WalletQuickPlusIcon size={18} /> },
              { label: t('phone.scanQr'), icon: <ScanQrIcon size={18} style={{ color: '#D4AF37' }} /> },
            ].map((a) => (
              <div key={a.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '15px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'grid',
                    placeItems: 'center',
                    lineHeight: 0,
                  }}
                >
                  {a.icon}
                </div>
                <span style={{ color: '#8B93A7', fontSize: '9px', fontWeight: '500', textAlign: 'center', lineHeight: 1.2 }}>{a.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '0 14px', flex: 1, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ color: '#F5F1EA', fontSize: '12px', fontWeight: '700', letterSpacing: '-0.3px' }}>{t('phone.recent')}</span>
            <span style={{ color: '#D4AF37', fontSize: '10px', fontWeight: '600' }}>{t('phone.seeAll')}</span>
          </div>
          <AnimatePresence initial={false}>
            {recents.slice(0, 3).map((tx, i) => (
              <motion.div
                key={tx.id}
                initial={{ opacity: 0, x: -12, height: 0 }}
                animate={{ opacity: 1, x: 0, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                style={{ overflow: 'hidden' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '13px',
                          background: tx.positive ? 'rgba(74,222,128,0.07)' : 'rgba(255,255,255,0.05)',
                          border: tx.positive ? '1px solid rgba(74,222,128,0.1)' : '1px solid rgba(255,255,255,0.07)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 0,
                        }}
                      >
                        <TxPoolGlyph
                          kind={tx.icon}
                          size={20}
                          stroke={tx.positive ? 'rgba(160, 220, 185, 0.92)' : 'rgba(220, 200, 150, 0.88)'}
                        />
                      </div>
                      {tx.positive && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '9px',
                            height: '9px',
                            borderRadius: '50%',
                            background: '#D4AF37',
                            border: '1.5px solid #0F0F0F',
                          }}
                        />
                      )}
                    </div>
                    <div>
                      <p style={{ color: '#F5F1EA', fontSize: '12px', margin: 0, fontWeight: '600', letterSpacing: '-0.2px' }}>
                        {t(`txPool.${tx.nameKey}`)}
                      </p>
                      <p style={{ color: '#8B93A7', fontSize: '9px', margin: 0 }}>{t(`phone.time.${tx.timeKey}`)}</p>
                    </div>
                  </div>
                  <span
                    style={{
                      color: tx.positive ? '#4ADE80' : '#F5F1EA',
                      fontSize: '12px',
                      fontWeight: '700',
                      letterSpacing: '-0.3px',
                      flexShrink: 0,
                      marginLeft: '6px',
                    }}
                  >
                    {tx.positive ? '+' : '−'}
                    {fmt(tx.amount)} MAD
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div style={{ padding: '6px 14px 10px', flexShrink: 0 }}>
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '100px',
              padding: '10px 20px',
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.8">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M14 14h2M18 14h2M14 18h2M18 18h2" />
              </svg>
            </div>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(212,175,55,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.8">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </svg>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.8">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroWalletPhoneScreen;
